// CDMLibrary.jsx  (React CLASS component + Tailwind)
// 2 tabs: Collection (CDMUser) + Library (CDMLibrary)
//
// Backend routes (withCredentials:true):
// Collection (private):
//   GET    /cdmUser/list
//   POST   /cdmUser/create
//   PATCH  /cdmUser/:id
//   DELETE /cdmUser/:id
//   POST   /cdmUser/:id/publish
//
// Library (public):
//   GET    /cdmLibrary/list
//   POST   /cdmLibrary/:id/like
//   POST   /cdmLibrary/:id/copyToUser
//
// NOTE:
// - Your backend JWT stores user id at req.userData.userId (server-side).
// - This client restores add/edit/delete for goals/todos/referrals/comments by PATCH-ing the whole array on CDMUser.

import React from "react";
import axios from "axios";
import { useAuth } from "../../utilities/AuthContext";
import { useNavigation } from "../../utilities/NavigationContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { showSuccessToast, showErrorToast } from "../../utilities/helper";

import { HandThumbUpIcon as HandThumbUpOutline } from "@heroicons/react/24/outline";
import { HandThumbUpIcon as HandThumbUpSolid } from "@heroicons/react/24/solid";

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

// If your server mounts under /api, change these to `${EndPoint}/api/cdmUser` etc.
const CDM_USER = `${EndPoint}/cdmUser`;
const CDM_LIBRARY = `${EndPoint}/cdmLibrary`;

class CDMLibrary extends React.Component {
  state = {
    activeTab: "collection", // collection | library

    // Current user display name (for new entries)
    author: "",

    // Collection (private)
    collection: [],
    collectionLoading: false,

    // Public library
    library: [],
    libraryLoading: false,

    // UI
    searchCollection: "",
    searchLibrary: "",
    filterCategory: "All",
    selectedCategory: "Chronic Disease",

    // Add/Edit template (title/category)
    newTitle: "",
    editingId: null,
    editingTitle: "",
    editingCategory: "",

    // Entry editing (goals/todos/referrals/comments)
    entryInputs: {}, // { `${templateId}_${type}`: "text" }  type: goal|todo|referral|comment
    editingEntries: {}, // { [entryId]: "text" }

    // Like state (local map: libraryTemplateId -> boolean)
    likedMap: {},

    // Optional: collapse/expand per template
    collapsed: {}, // { [templateId]: boolean }
  };

  categoryOptions = [
    { label: "Chronic Disease", color: "bg-sky-200" },
    { label: "Mental Health", color: "bg-purple-200" },
    { label: "Lifestyle", color: "bg-green-200" },
    { label: "Substance Use", color: "bg-red-200" },
    { label: "Preventive", color: "bg-orange-200" },
    { label: "Senior Health", color: "bg-slate-300" },
    { label: "Social", color: "bg-yellow-200" },
  ];

  componentDidMount() {
    this.fetchUserData();
    this.loadCollection();
    this.loadLibrary();
  }

  // ---------------------------
  // User
  // ---------------------------
  fetchUserData = async () => {
    try {
      const res = await axios.get(`${EndPoint}/user/user_detail`, {
        withCredentials: true,
      });
      const author = `${res.data.firstName || ""} ${res.data.lastName || ""}`.trim();
      this.setState({ author });
    } catch (err) {
      console.error("user_detail error:", err?.response?.data || err.message);
      // not fatal
    }
  };

  setTab = (tab) => this.setState({ activeTab: tab });

  toggleCollapse = (id) => {
    this.setState((prev) => ({
      collapsed: { ...prev.collapsed, [id]: !prev.collapsed[id] },
    }));
  };

  // ---------------------------
  // Collection (CDMUser)
  // ---------------------------
  loadCollection = async () => {
    try {
      this.setState({ collectionLoading: true });
      const res = await axios.get(`${CDM_USER}/list`, { withCredentials: true });
      const items = res.data.items || [];
      const collapsed = {};
      items.forEach((x) => (collapsed[x._id] = true));
      this.setState({ collection: items, collapsed });
    } catch (err) {
      console.error("CDMUser/list error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || "Failed to load your collection.");
    } finally {
      this.setState({ collectionLoading: false });
    }
  };

  addToCollection = async () => {
    const { newTitle, selectedCategory } = this.state;
    if (!newTitle.trim()) return;

    try {
      const payload = {
        title: newTitle.trim(),
        category: selectedCategory,
        goals: [],
        todos: [],
        comments: [],
        referrals: [],
      };

      const res = await axios.post(`${CDM_USER}/create`, payload, {
        withCredentials: true,
      });

      const item = res.data.item;

      this.setState((prev) => ({
        newTitle: "",
        collection: [item, ...prev.collection],
        collapsed: { ...prev.collapsed, [item._id]: true },
      }));

      showSuccessToast("Added to your collection.");
    } catch (err) {
      console.error("CDMUser/create error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || "Failed to add.");
    }
  };

  startEditTemplate = (item) => {
    this.setState({
      editingId: item._id,
      editingTitle: item.title || "",
      editingCategory: item.category || this.state.selectedCategory,
    });
  };

  cancelEditTemplate = () => {
    this.setState({ editingId: null, editingTitle: "", editingCategory: "" });
  };

  updateCollectionItem = async (id) => {
    const { editingTitle, editingCategory } = this.state;
    if (!editingTitle.trim()) return;

    try {
      const updates = { title: editingTitle.trim(), category: (editingCategory || "").trim() };
      const res = await axios.patch(`${CDM_USER}/${id}`, updates, { withCredentials: true });
      const updated = res.data.item;

      this.setState((prev) => ({
        editingId: null,
        editingTitle: "",
        editingCategory: "",
        collection: prev.collection.map((x) => (x._id === id ? updated : x)),
      }));

      showSuccessToast("Updated.");
    } catch (err) {
      console.error("CDMUser/update error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || "Failed to update.");
    }
  };

  deleteCollectionItem = async (id) => {
    try {
      await axios.delete(`${CDM_USER}/${id}`, { withCredentials: true });
      this.setState((prev) => ({
        collection: prev.collection.filter((x) => x._id !== id),
      }));
      showSuccessToast("Deleted.");
    } catch (err) {
      console.error("CDMUser/delete error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || "Failed to delete.");
    }
  };

  publishCollectionItem = async (id) => {
    try {
      const res = await axios.post(
        `${EndPoint}/cdmUser/${id}/publish`,
        {},
        { withCredentials: true }
      );

      this.setState((prev) => ({
        collection: prev.collection.map((x) =>
          x._id === id ? { ...x, isPublic: true } : x
        ),
      }));

      showSuccessToast(res.data?.message || "Published to public library.");
      this.loadLibrary();
    } catch (err) {
      console.error(err);
      showErrorToast("Failed to publish.");
    }
  };


  // ---------------------------
  // Entry-level CRUD (Collection only)
  // Implemented by PATCH-ing whole arrays to /cdmUser/:id
  // ---------------------------
  entryKey = (templateId, type) => `${templateId}_${type}`; // type: goal|todo|referral|comment
  fieldName = (type) => `${type}s`; // goals/todos/referrals/comments

  handleEntryInputChange = (templateId, type, value) => {
    const key = this.entryKey(templateId, type);
    this.setState((prev) => ({
      entryInputs: { ...prev.entryInputs, [key]: value },
    }));
  };

  startEditEntry = (entryId, text) => {
    this.setState((prev) => ({
      editingEntries: { ...prev.editingEntries, [entryId]: text },
    }));
  };

  cancelEditEntry = (entryId) => {
    this.setState((prev) => {
      const next = { ...prev.editingEntries };
      delete next[entryId];
      return { editingEntries: next };
    });
  };

  patchTemplateArray = async (templateId, field, nextArray) => {
    const res = await axios.patch(
      `${CDM_USER}/${templateId}`,
      { [field]: nextArray },
      { withCredentials: true }
    );
    return res.data.item;
  };

  addEntryToTemplate = async (templateId, type, event) => {
    if (event.key !== "Enter") return;

    const key = this.entryKey(templateId, type);
    const text = (this.state.entryInputs[key] || "").trim();
    if (!text) return;

    const field = this.fieldName(type);

    try {
      const template = this.state.collection.find((x) => x._id === templateId);
      if (!template) return;

      const newEntry = {
        entry: text,
        type, // schema requires enum
        author: this.state.author || "",
        dateAdded: new Date().toISOString(),
      };

      const nextArray = [ ...(template[field] || []), newEntry ];
      const updated = await this.patchTemplateArray(templateId, field, nextArray);

      this.setState((prev) => ({
        entryInputs: { ...prev.entryInputs, [key]: "" },
        collection: prev.collection.map((x) => (x._id === templateId ? updated : x)),
      }));

      showSuccessToast(`${type} added.`);
    } catch (err) {
      console.error("addEntry error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || `Failed to add ${type}.`);
    }
  };

  deleteEntryFromTemplate = async (templateId, type, entryId) => {
    const field = this.fieldName(type);

    try {
      const template = this.state.collection.find((x) => x._id === templateId);
      if (!template) return;

      const nextArray = (template[field] || []).filter((e) => String(e._id) !== String(entryId));
      const updated = await this.patchTemplateArray(templateId, field, nextArray);

      this.setState((prev) => ({
        collection: prev.collection.map((x) => (x._id === templateId ? updated : x)),
      }));

      showSuccessToast(`${type} deleted.`);
    } catch (err) {
      console.error("deleteEntry error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || `Failed to delete ${type}.`);
    }
  };

  saveEditedEntry = async (templateId, type, entryId, event) => {
    if (event.key !== "Enter") return;

    const newText = (this.state.editingEntries[entryId] || "").trim();
    if (!newText) return;

    const field = this.fieldName(type);

    try {
      const template = this.state.collection.find((x) => x._id === templateId);
      if (!template) return;

      const nextArray = (template[field] || []).map((e) =>
        String(e._id) === String(entryId) ? { ...e, entry: newText } : e
      );

      const updated = await this.patchTemplateArray(templateId, field, nextArray);

      this.setState((prev) => {
        const nextEditing = { ...prev.editingEntries };
        delete nextEditing[entryId];
        return {
          editingEntries: nextEditing,
          collection: prev.collection.map((x) => (x._id === templateId ? updated : x)),
        };
      });

      showSuccessToast(`${type} updated.`);
    } catch (err) {
      console.error("saveEditedEntry error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || `Failed to update ${type}.`);
    }
  };

  // ---------------------------
  // Library (CDMLibrary)
  // ---------------------------
  loadLibrary = async () => {
    try {
      this.setState({ libraryLoading: true });
      const res = await axios.get(`${CDM_LIBRARY}/list?sort=recent&limit=50`, {
        withCredentials: true,
      });
      const items = res.data.items || [];

      // default: collapsed = true for each library item
      const collapsed = { ...this.state.collapsed };
      items.forEach((x) => {
        if (collapsed[x._id] === undefined) collapsed[x._id] = true;
      });

      this.setState({ library: items, collapsed });
    } catch (err) {
      console.error("CDMLibrary/list error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || "Failed to load public library.");
    } finally {
      this.setState({ libraryLoading: false });
    }
  };

  toggleLike = async (id) => {
    try {
      const res = await axios.post(`${CDM_LIBRARY}/${id}/like`, {}, { withCredentials: true });
      const { liked, likes } = res.data;

      this.setState((prev) => ({
        likedMap: { ...prev.likedMap, [id]: Boolean(liked) },
        library: prev.library.map((x) =>
          x._id === id ? { ...x, likes: typeof likes === "number" ? likes : (x.likes || 0) } : x
        ),
      }));
    } catch (err) {
      console.error("CDMLibrary/like error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || "Failed to like.");
    }
  };


  deleteFromPublicLibrary = async (id) => {
    try {
      await axios.delete(`${CDM_LIBRARY}/${id}`, { withCredentials: true });
      this.setState((prev) => ({
        library: prev.library.filter((x) => x._id !== id),
      }));
      showSuccessToast("Deleted from public library.");
    } catch (err) {
      console.error("CDMLibrary/delete error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || "Failed to delete from public library.");
    }
  };


  copyToCollection = async (id) => {
    try {
      const res = await axios.post(`${CDM_LIBRARY}/${id}/copyToUser`, {}, { withCredentials: true });
      const copied = res.data.item;

      this.setState((prev) => ({
        collection: [copied, ...prev.collection],
        collapsed: { ...prev.collapsed, [copied._id]: true },
        activeTab: "collection",
      }));

      showSuccessToast(res?.data?.message || "Copied to your collection.");
    } catch (err) {
      console.error("CDMLibrary/copyToUser error:", err?.response?.data || err.message);
      showErrorToast(err?.response?.data?.message || "Failed to copy.");
    }
  };

  // ---------------------------
  // Render helpers
  // ---------------------------
  getCategoryChip = (category) => {
    const found = this.categoryOptions.find((c) => c.label === category) || this.categoryOptions[0];
    return (
      <span className={`px-3 py-1 rounded text-sm font-semibold text-slate-800 ${found.color}`}>
        {category || found.label}
      </span>
    );
  };

  renderReadOnlySections(item) {
    const renderList = (label, arr) => (
      <div className="bg-white rounded border p-3">
        <div className="text-sm font-semibold text-center bg-sky-700 text-white rounded py-2 mb-2">
          {label}
        </div>
        <div className="space-y-2">
          {(arr || []).length === 0 ? (
            <div className="text-sm text-slate-400 text-center">—</div>
          ) : (
            (arr || []).map((e) => (
              <div key={e._id || `${label}_${e.entry}`} className="bg-slate-50 rounded p-2 text-sm">
                <div className="text-slate-800">{e.entry}</div>
                <div className="text-xs italic text-slate-500 mt-1">
                  {e.dateAdded ? new Date(e.dateAdded).toLocaleString() : ""}{" "}
                  {e.author ? `· ${e.author}` : ""}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );

    return (
      <div className="grid grid-cols-4 gap-4 xs:grid-cols-2 sm:grid-cols-2 mt-4">
        {renderList("Goals", item.goals)}
        {renderList("Todos", item.todos)}
        {renderList("Referrals", item.referrals)}
        {renderList("Comments", item.comments)}
      </div>
    );
  }

  renderEditableSections(item) {
    const renderList = (label, type, arr) => {
      const key = this.entryKey(item._id, type);
      const items = arr || [];

      return (
        <div className="bg-white rounded border p-3">
          <div className="text-sm font-semibold text-center bg-sky-700 text-white rounded py-2 mb-2">
            {label}
          </div>

          <div className="space-y-2">
            {items.length === 0 ? (
              <div className="text-sm text-slate-400 text-center">—</div>
            ) : (
              items.map((e) => {
                const isEditing = this.state.editingEntries[e._id] !== undefined;

                return (
                  <div key={e._id} className="bg-slate-50 rounded p-2 text-sm flex gap-2 items-start">
                    <div className="flex-1">
                      {isEditing ? (
                        <input
                          className="w-full border rounded p-2"
                          value={this.state.editingEntries[e._id]}
                          onChange={(ev) =>
                            this.setState((prev) => ({
                              editingEntries: { ...prev.editingEntries, [e._id]: ev.target.value },
                            }))
                          }
                          onKeyDown={(ev) => this.saveEditedEntry(item._id, type, e._id, ev)}
                        />
                      ) : (
                        <div
                          className="cursor-pointer bg-slate-100 rounded p-2"
                          onDoubleClick={() => this.startEditEntry(e._id, e.entry)}
                        >
                          <div className="text-slate-800">{e.entry}</div>
                          <div className="text-xs italic text-slate-500 mt-1">
                            {e.dateAdded ? new Date(e.dateAdded).toLocaleString() : ""}{" "}
                            {e.author ? `· ${e.author}` : ""}
                          </div>
                        </div>
                      )}
                    </div>

                    {isEditing ? (
                      <button
                        className="text-slate-500 hover:text-slate-800 text-xs mt-2"
                        onClick={() => this.cancelEditEntry(e._id)}
                      >
                        Cancel
                      </button>
                    ) : (
                      <button
                        className="text-red-500 hover:text-red-700 text-xs mt-2"
                        onClick={() => this.deleteEntryFromTemplate(item._id, type, e._id)}
                        title="Delete"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <input
            className="w-full border rounded p-2 mt-3 text-sm"
            placeholder={`Type ${type} and press Enter…`}
            value={this.state.entryInputs[key] || ""}
            onChange={(e) => this.handleEntryInputChange(item._id, type, e.target.value)}
            onKeyDown={(e) => this.addEntryToTemplate(item._id, type, e)}
          />
        </div>
      );
    };

    return (
      <div className="grid grid-cols-4 gap-4 xs:grid-cols-2 sm:grid-cols-2 mt-4">
        {renderList("Goals", "goal", item.goals)}
        {renderList("Todos", "todo", item.todos)}
        {renderList("Referrals", "referral", item.referrals)}
        {renderList("Comments", "comment", item.comments)}
      </div>
    );
  }

  // ---------------------------
  // Render main sections
  // ---------------------------
  renderCollection() {
    const { collection, collectionLoading, searchCollection, filterCategory, editingId, collapsed } = this.state;

    const filtered = (collection || [])
      .filter((x) => {
        const matchesCat = filterCategory === "All" || (x.category || "") === filterCategory;
        const q = searchCollection.trim().toLowerCase();
        const matchesQ = !q || (x.title || "").toLowerCase().includes(q);
        return matchesCat && matchesQ;
      })
      .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));

    return (
      <div className="mt-6">
        {/* Add new */}
        <div className="bg-white border rounded p-4 mb-4">
          <div className="grid grid-cols-4 gap-3 xs:grid-cols-1 sm:grid-cols-1 items-center">
            <div className="font-semibold text-slate-700">Add new template</div>
            <input
              className="col-span-2 border rounded p-2"
              placeholder="Template title (e.g. Diabetes annual review)"
              value={this.state.newTitle}
              onChange={(e) => this.setState({ newTitle: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") this.addToCollection();
              }}
            />
            <div className="flex gap-2">
              <select
                value={this.state.selectedCategory}
                onChange={(e) => this.setState({ selectedCategory: e.target.value })}
                className="border rounded p-2"
              >
                {this.categoryOptions.map((c) => (
                  <option key={c.label}>{c.label}</option>
                ))}
              </select>
              <button
                onClick={this.addToCollection}
                className="bg-sky-700 hover:bg-sky-800 text-white px-4 py-2 rounded font-semibold"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center mb-4">
          <div className="font-semibold text-slate-700">Filter:</div>
          <select
            value={filterCategory}
            onChange={(e) => this.setState({ filterCategory: e.target.value })}
            className="border rounded p-2"
          >
            <option>All</option>
            {this.categoryOptions.map((c) => (
              <option key={c.label}>{c.label}</option>
            ))}
          </select>

          <input
            className="border rounded p-2 w-80 xs:w-full sm:w-full"
            placeholder="Search your collection…"
            value={searchCollection}
            onChange={(e) => this.setState({ searchCollection: e.target.value })}
          />
          <button
            onClick={this.loadCollection}
            className="border rounded px-3 py-2 text-slate-700 hover:border-sky-400"
          >
            Refresh
          </button>
        </div>

        {collectionLoading ? (
          <div className="text-slate-500">Loading…</div>
        ) : (
          <div className="space-y-4">
            {filtered.map((item) => {
              const isEditing = editingId === item._id;
              const isCollapsed = collapsed[item._id] !== false; // default true

              return (
                <div key={item._id} className="bg-slate-100 border rounded p-4 shadow">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-3">
                      {isEditing ? (
                        <>
                          <input
                            className="border rounded p-2 font-bold text-slate-800 w-[420px] xs:w-full sm:w-full"
                            value={this.state.editingTitle}
                            onChange={(e) => this.setState({ editingTitle: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") this.updateCollectionItem(item._id);
                            }}
                          />
                          <select
                            className="border rounded p-2"
                            value={this.state.editingCategory}
                            onChange={(e) => this.setState({ editingCategory: e.target.value })}
                          >
                            {this.categoryOptions.map((c) => (
                              <option key={c.label}>{c.label}</option>
                            ))}
                          </select>
                        </>
                      ) : (
                        <>
                          <div className="text-xl font-bold text-slate-800">{item.title}</div>
                          {this.getCategoryChip(item.category)}
                          <button
                            onClick={() => this.toggleCollapse(item._id)}
                            className="text-sm text-slate-600 hover:text-sky-700 border rounded px-3 py-2"
                          >
                            {isCollapsed ? "Expand" : "Collapse"}
                          </button>
                        </>
                      )}
                    </div>

                    <div className="flex gap-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => this.updateCollectionItem(item._id)}
                            className="bg-sky-700 hover:bg-sky-800 text-white px-3 py-2 rounded font-semibold"
                          >
                            Update
                          </button>
                          <button
                            onClick={this.cancelEditTemplate}
                            className="border px-3 py-2 rounded hover:border-sky-400"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => this.startEditTemplate(item)}
                            className="border px-3 py-2 rounded hover:border-sky-400"
                          >
                            Update
                          </button>
                        {item.isPublic ? (
  <button
    disabled
    className="bg-slate-300 text-slate-600 px-3 py-2 rounded font-semibold cursor-not-allowed"
    title="Already published"
  >
    Published
  </button>
) : (
  <button
    onClick={() => this.publishCollectionItem(item._id)}
    className="bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-2 rounded font-semibold"
  >
    Publish
  </button>
)}

                          <button
                            onClick={() => this.deleteCollectionItem(item._id)}
                            className="border border-red-300 text-red-700 px-3 py-2 rounded hover:border-red-500"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {!isCollapsed && this.renderEditableSections(item)}
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div className="text-slate-500 text-center py-10">No templates found.</div>
            )}
          </div>
        )}
      </div>
    );
  }

  renderPublicLibrary() {
    const { library, libraryLoading, searchLibrary, filterCategory, likedMap, author } = this.state;
    const currentUserId =
    this.props?.auth?.[0]?._id ||
    this.props?.auth?.[0]?.userId;

    const filtered = (library || [])
      .filter((x) => {
        const matchesCat = filterCategory === "All" || (x.category || "") === filterCategory;
        const q = searchLibrary.trim().toLowerCase();
        const matchesQ = !q || (x.title || "").toLowerCase().includes(q);
        return matchesCat && matchesQ;
      })
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));


    return (
      <div className="mt-6">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center mb-4">
          <div className="font-semibold text-slate-700">Filter:</div>
          <select
            value={filterCategory}
            onChange={(e) => this.setState({ filterCategory: e.target.value })}
            className="border rounded p-2"
          >
            <option>All</option>
            {this.categoryOptions.map((c) => (
              <option key={c.label}>{c.label}</option>
            ))}
          </select>

          <input
            className="border rounded p-2 w-80 xs:w-full sm:w-full"
            placeholder="Search public library…"
            value={searchLibrary}
            onChange={(e) => this.setState({ searchLibrary: e.target.value })}
          />

          <button
            onClick={this.loadLibrary}
            className="border rounded px-3 py-2 text-slate-700 hover:border-sky-400"
          >
            Refresh
          </button>
        </div>

        {libraryLoading ? (
          <div className="text-slate-500">Loading…</div>
        ) : (
          <div className="space-y-4">
           {filtered.map((item) => {
              const liked = Boolean(likedMap[item._id]);
              const LikeIcon = liked ? HandThumbUpSolid : HandThumbUpOutline;

              const isCollapsed = this.state.collapsed[item._id] !== false; // default true

              return (
                <div key={item._id} className="bg-slate-100 border rounded p-4 shadow">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="text-xl font-bold text-slate-800">{item.title}</div>
                      {this.getCategoryChip(item.category)}
                      <div className="text-sm text-slate-500">
                        {item.author ? `by ${item.author}` : ""}
                      </div>

                      {/* ✅ Expand/Collapse */}
                      <button
                        onClick={() => this.toggleCollapse(item._id)}
                        className="text-sm text-slate-600 hover:text-sky-700 border rounded px-3 py-2"
                      >
                        {isCollapsed ? "Expand" : "Collapse"}
                      </button>
                    </div>

                    <div className="flex gap-2 items-center">
                      {item.author !== this.state.author && (
                      <>
                      <button
                        onClick={() => this.toggleLike(item._id)}
                        className="inline-flex items-center gap-2 border rounded px-3 py-2 hover:border-sky-400"
                        title="Like"
                      >
                        <LikeIcon className="h-5 w-5" />
                        <span className="text-sm font-semibold">{item.likes || 0}</span>
                      </button>

                      <button
                        onClick={() => this.copyToCollection(item._id)}
                        className="bg-sky-700 hover:bg-sky-800 text-white px-3 py-2 rounded font-semibold"
                        title="Copy to my collection"
                      >
                        Copy
                      </button>
                      </>
                        )}
                     {item.author === this.state.author && (
                        <button
                          onClick={() => this.deleteFromPublicLibrary(item._id)}
                          className="border border-red-300 text-red-700 px-3 py-2 rounded hover:border-red-500"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  {/* ✅ Only show sections when expanded */}
                  {!isCollapsed && this.renderReadOnlySections(item)}
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div className="text-slate-500 text-center py-10">No templates found.</div>
            )}
          </div>
        )}
      </div>
    );
  }

  render() {
    const { activeTab } = this.state;

    const tabBase = "px-4 py-2 rounded-lg font-semibold border transition";
    const tabOn = "bg-sky-800 text-white border-sky-800";
    const tabOff = "bg-white text-slate-700 border-slate-300 hover:border-sky-400";

    return (
      <>
        <div className="p-8 mx-auto">
          <h1 className="text-5xl text-center text-sky-800 font-bold mb-6 xs:text-3xl sm:text-3xl">
            Chronic Disease Management Library
          </h1>

          {/* Tabs */}
          <div className="flex gap-3 justify-center">
            <button
              className={`${tabBase} ${activeTab === "collection" ? tabOn : tabOff}`}
              onClick={() => this.setTab("collection")}
            >
              Collection
            </button>
            <button
              className={`${tabBase} ${activeTab === "library" ? tabOn : tabOff}`}
              onClick={() => this.setTab("library")}
            >
              Library
            </button>
          </div>

          {/* Content */}
          {activeTab === "collection" ? this.renderCollection() : this.renderPublicLibrary()}

          <ToastContainer />
        </div>
      </>
    );
  }
}

const CDMLibraryWithNavigation = (props) => {
  const auth = useAuth();
  const navigate = useNavigation();
  return <CDMLibrary {...props} auth={auth} navigate={navigate} />;
};

export default CDMLibraryWithNavigation;
